import React, { useCallback, useEffect, useState } from "react";
import {
    Bell,
    Plus,
    Trash2,
    Edit3,
    CheckCheck,
    ExternalLink,
    DownloadCloud,
    Building2,
    AlertCircle,
    RefreshCw,
    Save,
    Trophy,
} from "lucide-react";
import { Layout } from "@/components/Layout";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import {
    Dialog,
    DialogContent,
    DialogDescription,
    DialogFooter,
    DialogHeader,
    DialogTitle,
} from "@/components/ui/dialog";
import {
    listWatchlists,
    createWatchlist,
    updateWatchlistApi,
    deleteWatchlist,
    checkWatchlist,
    listWatchlistAlerts,
    markAlertRead,
    markAllAlertsRead,
    importFromPlcsp,
    listCompetitorAlerts,
    markCompetitorAlertRead,
    markAllCompetitorAlertsRead,
} from "@/lib/api";
import { fmtEUR } from "@/lib/format";
import { toast } from "sonner";
import { useNavigate } from "react-router-dom";

export default function Alerts() {
    const navigate = useNavigate();
    const [tab, setTab] = useState("alerts"); // alerts | competitors | watchlists
    const [watchlists, setWatchlists] = useState([]);
    const [alerts, setAlerts] = useState([]);
    const [unread, setUnread] = useState(0);
    const [competitorAlerts, setCompetitorAlerts] = useState([]);
    const [competitorUnread, setCompetitorUnread] = useState(0);
    const [loading, setLoading] = useState(true);
    const [editing, setEditing] = useState(undefined);
    const [form, setForm] = useState({
        name: "",
        q: "",
        cpv: "",
        min_budget: "",
        max_budget: "",
        state: "",
    });

    const reload = useCallback(async () => {
        setLoading(true);
        try {
            const [wls, al, cal] = await Promise.all([
                listWatchlists(),
                listWatchlistAlerts(false),
                listCompetitorAlerts(false),
            ]);
            setWatchlists(wls);
            setAlerts(al.alerts || []);
            setUnread(al.unread_count || 0);
            setCompetitorAlerts(cal.alerts || []);
            setCompetitorUnread(cal.unread_count || 0);
        } finally {
            setLoading(false);
        }
    }, []);

    useEffect(() => {
        reload();
    }, [reload]);

    const openCreate = () => {
        setEditing(null);
        setForm({
            name: "",
            q: "",
            cpv: "",
            min_budget: "",
            max_budget: "",
            state: "",
        });
    };
    const openEdit = (w) => {
        setEditing(w);
        setForm({
            name: w.name,
            q: w.q || "",
            cpv: w.cpv || "",
            min_budget: w.min_budget != null ? String(w.min_budget) : "",
            max_budget: w.max_budget != null ? String(w.max_budget) : "",
            state: w.state || "",
        });
    };

    const onSave = async () => {
        if (!form.name.trim()) {
            toast.error("Indica un nombre");
            return;
        }
        const payload = {
            name: form.name.trim(),
            q: form.q.trim(),
            cpv: form.cpv.trim(),
            min_budget: form.min_budget ? Number(form.min_budget) : null,
            max_budget: form.max_budget ? Number(form.max_budget) : null,
            state: form.state,
        };
        try {
            if (editing) {
                await updateWatchlistApi(editing.id, payload);
                toast.success("Búsqueda actualizada");
            } else {
                await createWatchlist(payload);
                toast.success("Búsqueda guardada — recibirás novedades automáticamente");
            }
            setEditing(undefined);
            await reload();
        } catch {
            toast.error("No se pudo guardar");
        }
    };

    const onDelete = async (w) => {
        if (!window.confirm(`¿Eliminar "${w.name}" y sus alertas?`)) return;
        await deleteWatchlist(w.id);
        toast.success("Eliminada");
        reload();
    };

    const onCheck = async (w) => {
        try {
            const r = await checkWatchlist(w.id);
            if (r.new_alerts > 0) toast.success(`${r.new_alerts} novedad${r.new_alerts > 1 ? "es" : ""}`);
            else toast.info("Sin novedades por ahora");
            reload();
        } catch {
            toast.error("No se pudo comprobar ahora");
        }
    };

    const onMarkRead = async (a) => {
        if (a.read) return;
        await markAlertRead(a.id);
        setAlerts((prev) =>
            prev.map((x) => (x.id === a.id ? { ...x, read: true } : x))
        );
        setUnread((u) => Math.max(0, u - 1));
    };

    const onMarkAllRead = async () => {
        await markAllAlertsRead();
        setAlerts((prev) => prev.map((x) => ({ ...x, read: true })));
        setUnread(0);
        toast.success("Todas marcadas como leídas");
    };

    const onMarkCompetitorRead = async (a) => {
        if (a.read) return;
        await markCompetitorAlertRead(a.id);
        setCompetitorAlerts((prev) =>
            prev.map((x) => (x.id === a.id ? { ...x, read: true } : x))
        );
        setCompetitorUnread((u) => Math.max(0, u - 1));
    };

    const onMarkAllCompetitorRead = async () => {
        await markAllCompetitorAlertsRead();
        setCompetitorAlerts((prev) => prev.map((x) => ({ ...x, read: true })));
        setCompetitorUnread(0);
        toast.success("Todas marcadas como leídas");
    };

    const onImport = async (a, withPdfs = true) => {
        try {
            const t = await importFromPlcsp(a.external_id, withPdfs);
            const pdfCount = (t.documents || []).length;
            toast.success(
                withPdfs && pdfCount > 0
                    ? `Importada con ${pdfCount} documento${pdfCount === 1 ? "" : "s"}`
                    : "Importada en tu panel",
                {
                    action: {
                        label: "Abrir",
                        onClick: () => navigate(`/app/licitacion/${t.id}`),
                    },
                }
            );
            await markAlertRead(a.id);
            setAlerts((prev) =>
                prev.map((x) => (x.id === a.id ? { ...x, read: true } : x))
            );
            setUnread((u) => Math.max(0, u - 1));
        } catch (e) {
            toast.error(e?.response?.data?.detail || "No se pudo importar");
        }
    };

    return (
        <Layout>
            <div className="max-w-[1200px] mx-auto p-6 md:p-8" data-testid="alerts-page">
                {/* Header */}
                <div className="flex flex-col md:flex-row md:items-end md:justify-between gap-4 mb-8">
                    <div>
                        <div className="text-[11px] font-bold uppercase tracking-[0.2em] text-brand-600 mb-2">
                            — Vigilancia continua
                        </div>
                        <h1 className="font-display text-4xl md:text-5xl tracking-tight font-black text-ink-950 leading-[1.05]">
                            Alertas PLCSP
                        </h1>
                        <p className="text-base text-ink-500 mt-2 font-medium max-w-2xl">
                            Guarda búsquedas frecuentes y recibe novedades cuando aparezcan
                            licitaciones que cumplan tus criterios.
                        </p>
                    </div>
                    <Button
                        onClick={openCreate}
                        className="rounded-full bg-brand-600 hover:bg-brand-700 text-white h-11 px-5 font-bold shadow-pop hover:scale-[1.03] transition-all"
                        data-testid="alerts-new-btn"
                    >
                        <Plus size={15} className="mr-1.5" />
                        Nueva búsqueda guardada
                    </Button>
                </div>

                {/* Tabs */}
                <div className="inline-flex bg-white border border-ink-100 p-1 rounded-xl mb-6 shadow-pop">
                    <button
                        onClick={() => setTab("alerts")}
                        className={`flex items-center gap-2 px-5 h-10 text-sm font-bold rounded-lg transition-all ${
                            tab === "alerts"
                                ? "bg-brand-600 text-white shadow-pop"
                                : "text-ink-500 hover:bg-brand-50 hover:text-brand-600"
                        }`}
                        data-testid="alerts-tab-novedades"
                    >
                        <Bell size={14} />
                        Novedades
                        {unread > 0 && (
                            <span className="ml-1 bg-red-500 text-white text-[10px] font-bold rounded-full min-w-[20px] h-5 px-1.5 flex items-center justify-center">
                                {unread}
                            </span>
                        )}
                    </button>
                    <button
                        onClick={() => setTab("competitors")}
                        className={`flex items-center gap-2 px-5 h-10 text-sm font-bold rounded-lg transition-all ${
                            tab === "competitors"
                                ? "bg-amber-600 text-white shadow-pop"
                                : "text-ink-500 hover:bg-amber-50 hover:text-amber-700"
                        }`}
                        data-testid="alerts-tab-competidores"
                    >
                        <Trophy size={14} />
                        Competidores
                        {competitorUnread > 0 && (
                            <span className="ml-1 bg-red-500 text-white text-[10px] font-bold rounded-full min-w-[20px] h-5 px-1.5 flex items-center justify-center">
                                {competitorUnread}
                            </span>
                        )}
                    </button>
                    <button
                        onClick={() => setTab("watchlists")}
                        className={`flex items-center gap-2 px-5 h-10 text-sm font-bold rounded-lg transition-all ${
                            tab === "watchlists"
                                ? "bg-brand-600 text-white shadow-pop"
                                : "text-ink-500 hover:bg-brand-50 hover:text-brand-600"
                        }`}
                        data-testid="alerts-tab-busquedas"
                    >
                        <Save size={14} />
                        Búsquedas guardadas ({watchlists.length})
                    </button>
                </div>

                {tab === "alerts" && (
                    <AlertsList
                        alerts={alerts}
                        loading={loading}
                        onMarkRead={onMarkRead}
                        onMarkAllRead={onMarkAllRead}
                        onImport={onImport}
                        unread={unread}
                    />
                )}
                {tab === "competitors" && (
                    <CompetitorAlertsList
                        alerts={competitorAlerts}
                        loading={loading}
                        onMarkRead={onMarkCompetitorRead}
                        onMarkAllRead={onMarkAllCompetitorRead}
                        unread={competitorUnread}
                    />
                )}
                {tab === "watchlists" && (
                    <WatchlistsList
                        watchlists={watchlists}
                        loading={loading}
                        onEdit={openEdit}
                        onDelete={onDelete}
                        onCheck={onCheck}
                    />
                )}
            </div>

            {/* Create/Edit modal */}
            <Dialog
                open={editing !== undefined}
                onOpenChange={(v) => !v && setEditing(undefined)}
            >
                <DialogContent className="rounded-2xl max-w-lg">
                    <DialogHeader>
                        <DialogTitle className="font-display text-2xl font-black">
                            {editing ? "Editar búsqueda" : "Nueva búsqueda guardada"}
                        </DialogTitle>
                        <DialogDescription>
                            Recibirás una alerta automática cada vez que aparezca una
                            licitación que cumpla estos criterios.
                        </DialogDescription>
                    </DialogHeader>
                    <div className="space-y-4 py-2">
                        <Field label="Nombre *">
                            <Input
                                value={form.name}
                                onChange={(e) =>
                                    setForm({ ...form, name: e.target.value })
                                }
                                placeholder="Obras de mantenimiento Madrid"
                                className="rounded-xl border-ink-100 h-11 font-medium"
                                data-testid="alert-form-name"
                            />
                        </Field>
                        <Field label="Palabra clave">
                            <Input
                                value={form.q}
                                onChange={(e) => setForm({ ...form, q: e.target.value })}
                                placeholder="mantenimiento, software…"
                                className="rounded-xl border-ink-100 h-11 font-medium"
                                data-testid="alert-form-q"
                            />
                        </Field>
                        <div className="grid grid-cols-2 gap-3">
                            <Field label="CPV (prefijo)">
                                <Input
                                    value={form.cpv}
                                    onChange={(e) =>
                                        setForm({ ...form, cpv: e.target.value })
                                    }
                                    placeholder="50000000"
                                    className="rounded-xl border-ink-100 h-11 font-mono"
                                    data-testid="alert-form-cpv"
                                />
                            </Field>
                            <Field label="Estado">
                                <select
                                    value={form.state}
                                    onChange={(e) =>
                                        setForm({ ...form, state: e.target.value })
                                    }
                                    className="w-full rounded-xl border border-ink-100 h-11 px-3 font-medium bg-white"
                                    data-testid="alert-form-state"
                                >
                                    <option value="">Cualquiera</option>
                                    <option value="PUB">Publicada</option>
                                    <option value="EVL">En evaluación</option>
                                    <option value="RES">Resuelta</option>
                                    <option value="ADJ">Adjudicada</option>
                                </select>
                            </Field>
                        </div>
                        <div className="grid grid-cols-2 gap-3">
                            <Field label="Mín. €">
                                <Input
                                    type="number"
                                    value={form.min_budget}
                                    onChange={(e) =>
                                        setForm({ ...form, min_budget: e.target.value })
                                    }
                                    placeholder="0"
                                    className="rounded-xl border-ink-100 h-11 font-mono"
                                    data-testid="alert-form-min"
                                />
                            </Field>
                            <Field label="Máx. €">
                                <Input
                                    type="number"
                                    value={form.max_budget}
                                    onChange={(e) =>
                                        setForm({ ...form, max_budget: e.target.value })
                                    }
                                    placeholder="∞"
                                    className="rounded-xl border-ink-100 h-11 font-mono"
                                    data-testid="alert-form-max"
                                />
                            </Field>
                        </div>
                    </div>
                    <DialogFooter>
                        <Button
                            variant="outline"
                            className="rounded-xl"
                            onClick={() => setEditing(undefined)}
                        >
                            Cancelar
                        </Button>
                        <Button
                            onClick={onSave}
                            className="rounded-xl bg-brand-600 hover:bg-brand-700 font-bold"
                            data-testid="alert-form-save"
                        >
                            <Save size={14} className="mr-1.5" />
                            Guardar
                        </Button>
                    </DialogFooter>
                </DialogContent>
            </Dialog>
        </Layout>
    );
}

const Field = ({ label, children }) => (
    <div>
        <Label className="text-[10px] font-bold uppercase tracking-[0.18em] text-ink-400 block mb-1.5">
            {label}
        </Label>
        {children}
    </div>
);

const AlertsList = ({ alerts, loading, onMarkRead, onMarkAllRead, onImport, unread }) => {
    if (loading) {
        return (
            <div className="bg-white rounded-2xl border border-ink-100 p-12 text-center text-ink-400">
                Cargando…
            </div>
        );
    }
    if (alerts.length === 0) {
        return (
            <div className="bg-white rounded-2xl border border-ink-100 p-12 text-center shadow-pop">
                <Bell className="mx-auto text-ink-300 mb-3" size={36} strokeWidth={1.5} />
                <div className="font-display text-xl font-black mb-1">
                    Sin novedades por ahora
                </div>
                <div className="text-sm text-ink-500 font-medium">
                    Crea búsquedas guardadas en la pestaña <b>Búsquedas guardadas</b> y
                    recibirás aquí las nuevas licitaciones que cumplan tus criterios.
                </div>
            </div>
        );
    }
    return (
        <>
            {unread > 0 && (
                <div className="flex justify-end mb-3">
                    <Button
                        variant="outline"
                        onClick={onMarkAllRead}
                        className="rounded-full border-ink-200 h-9 px-4 font-bold gap-1.5"
                        data-testid="mark-all-read-btn"
                    >
                        <CheckCheck size={14} />
                        Marcar todas como leídas
                    </Button>
                </div>
            )}
            <div className="space-y-3">
                {alerts.map((a) => (
                    <div
                        key={a.id}
                        onClick={() => onMarkRead(a)}
                        className={`group bg-white border rounded-2xl p-5 shadow-pop hover:shadow-pop-lg transition-all cursor-pointer ${
                            a.read
                                ? "border-ink-100 opacity-75"
                                : "border-brand-200 ring-2 ring-brand-100"
                        }`}
                        data-testid={`alert-${a.id}`}
                    >
                        <div className="flex flex-col md:flex-row md:items-start gap-4">
                            <div className="flex-1 min-w-0">
                                <div className="flex items-center gap-2 mb-2 flex-wrap">
                                    {!a.read && (
                                        <span className="w-2 h-2 rounded-full bg-brand-600 animate-pulse" />
                                    )}
                                    <span className="text-[10px] font-bold uppercase tracking-wider px-2.5 py-1 rounded-full bg-brand-50 text-brand-700 border border-brand-100">
                                        {a.watchlist_name}
                                    </span>
                                    {a.expediente && (
                                        <span className="text-[10px] font-mono font-bold text-ink-500 bg-ink-50 px-2 py-1 rounded-full">
                                            {a.expediente}
                                        </span>
                                    )}
                                </div>
                                <h3 className="font-display text-lg font-bold text-ink-950 leading-snug mb-2 line-clamp-2">
                                    {a.title}
                                </h3>
                                <div className="flex items-start gap-1.5 text-sm text-ink-500 font-medium mb-1">
                                    <Building2 size={12} className="text-ink-400 mt-0.5 shrink-0" />
                                    <span className="line-clamp-1">{a.organo || "—"}</span>
                                </div>
                                <div className="flex items-center gap-3 text-sm">
                                    <span className="font-mono font-bold text-ink-950">
                                        {fmtEUR(a.budget)}
                                    </span>
                                    {a.link && (
                                        <a
                                            href={a.link}
                                            target="_blank"
                                            rel="noopener noreferrer"
                                            onClick={(e) => e.stopPropagation()}
                                            className="inline-flex items-center gap-1 text-brand-600 font-bold hover:underline"
                                        >
                                            Ver en PLCSP
                                            <ExternalLink size={11} />
                                        </a>
                                    )}
                                </div>
                            </div>
                            <Button
                                onClick={(e) => {
                                    e.stopPropagation();
                                    onImport(a);
                                }}
                                className="rounded-full bg-brand-600 hover:bg-brand-700 text-white h-9 px-4 font-bold shrink-0"
                                data-testid={`alert-import-${a.id}`}
                            >
                                <DownloadCloud size={13} className="mr-1.5" />
                                Importar
                            </Button>
                        </div>
                    </div>
                ))}
            </div>
        </>
    );
};

const WatchlistsList = ({ watchlists, loading, onEdit, onDelete, onCheck }) => {
    if (loading) {
        return (
            <div className="bg-white rounded-2xl border border-ink-100 p-12 text-center text-ink-400">
                Cargando…
            </div>
        );
    }
    if (watchlists.length === 0) {
        return (
            <div className="bg-white rounded-2xl border border-ink-100 p-12 text-center shadow-pop">
                <Save className="mx-auto text-ink-300 mb-3" size={36} strokeWidth={1.5} />
                <div className="font-display text-xl font-black mb-1">
                    Aún no tienes búsquedas guardadas
                </div>
                <div className="text-sm text-ink-500 font-medium mb-4">
                    Crea una para empezar a recibir alertas cuando aparezcan licitaciones
                    que coincidan.
                </div>
            </div>
        );
    }
    return (
        <div className="space-y-3">
            {watchlists.map((w) => (
                <div
                    key={w.id}
                    className="bg-white border border-ink-100 rounded-2xl p-5 shadow-pop hover:shadow-pop-lg transition-all"
                    data-testid={`watchlist-${w.id}`}
                >
                    <div className="flex items-start justify-between gap-3">
                        <div className="min-w-0 flex-1">
                            <div className="font-display text-lg font-bold text-ink-950 mb-1">
                                {w.name}
                            </div>
                            <div className="flex flex-wrap items-center gap-2">
                                {w.q && (
                                    <span className="text-xs font-bold bg-ink-50 text-ink-700 px-2.5 py-1 rounded-full">
                                        kw: {w.q}
                                    </span>
                                )}
                                {w.cpv && (
                                    <span className="text-xs font-mono font-bold bg-brand-50 text-brand-700 px-2.5 py-1 rounded-full">
                                        CPV {w.cpv}
                                    </span>
                                )}
                                {w.state && (
                                    <span className="text-xs font-bold bg-emerald-50 text-emerald-700 px-2.5 py-1 rounded-full">
                                        {w.state}
                                    </span>
                                )}
                                {(w.min_budget != null || w.max_budget != null) && (
                                    <span className="text-xs font-mono font-bold bg-ink-50 text-ink-700 px-2.5 py-1 rounded-full">
                                        {w.min_budget != null ? fmtEUR(w.min_budget) : "0"}
                                        {" — "}
                                        {w.max_budget != null ? fmtEUR(w.max_budget) : "∞"}
                                    </span>
                                )}
                                {!w.q &&
                                    !w.cpv &&
                                    !w.state &&
                                    w.min_budget == null &&
                                    w.max_budget == null && (
                                        <span className="text-xs text-ink-400 font-medium italic">
                                            (sin filtros — recibirás todas las novedades)
                                        </span>
                                    )}
                            </div>
                        </div>
                        <div className="flex items-center gap-1.5 shrink-0">
                            <Button
                                variant="outline"
                                size="sm"
                                onClick={() => onCheck(w)}
                                className="rounded-full border-ink-200 h-9 w-9 p-0"
                                title="Comprobar ahora"
                                data-testid={`watchlist-check-${w.id}`}
                            >
                                <RefreshCw size={14} />
                            </Button>
                            <Button
                                variant="outline"
                                size="sm"
                                onClick={() => onEdit(w)}
                                className="rounded-full border-ink-200 h-9 w-9 p-0"
                                title="Editar"
                                data-testid={`watchlist-edit-${w.id}`}
                            >
                                <Edit3 size={14} />
                            </Button>
                            <Button
                                variant="outline"
                                size="sm"
                                onClick={() => onDelete(w)}
                                className="rounded-full border-red-200 text-red-600 hover:bg-red-50 h-9 w-9 p-0"
                                title="Eliminar"
                                data-testid={`watchlist-delete-${w.id}`}
                            >
                                <Trash2 size={14} />
                            </Button>
                        </div>
                    </div>
                </div>
            ))}
        </div>
    );
};

const CompetitorAlertsList = ({ alerts, loading, onMarkRead, onMarkAllRead, unread }) => {
    if (loading) {
        return (
            <div className="bg-white rounded-2xl border border-ink-100 p-12 text-center text-ink-400">
                Cargando…
            </div>
        );
    }
    if (alerts.length === 0) {
        return (
            <div
                className="bg-white rounded-2xl border border-ink-100 p-12 text-center shadow-pop"
                data-testid="competitor-alerts-empty"
            >
                <Trophy
                    className="mx-auto text-ink-300 mb-3"
                    size={36}
                    strokeWidth={1.5}
                />
                <div className="font-display text-xl font-black mb-1">
                    Sin nuevas adjudicaciones de tus competidores
                </div>
                <div className="text-sm text-ink-500 font-medium">
                    Guarda empresas en <b>Competidores</b> y recibirás aquí una alerta
                    cada vez que ganen una nueva licitación en la PLCSP.
                </div>
            </div>
        );
    }
    return (
        <>
            {unread > 0 && (
                <div className="flex justify-end mb-3">
                    <Button
                        variant="outline"
                        onClick={onMarkAllRead}
                        className="rounded-full border-ink-200 h-9 px-4 font-bold gap-1.5"
                        data-testid="competitor-mark-all-read-btn"
                    >
                        <CheckCheck size={14} />
                        Marcar todas como leídas
                    </Button>
                </div>
            )}
            <div className="space-y-3">
                {alerts.map((a) => (
                    <div
                        key={a.id}
                        onClick={() => onMarkRead(a)}
                        className={`group bg-white border rounded-2xl p-5 shadow-pop hover:shadow-pop-lg transition-all cursor-pointer ${
                            a.read
                                ? "border-ink-100 opacity-75"
                                : "border-amber-200 ring-2 ring-amber-100"
                        }`}
                        data-testid={`competitor-alert-${a.id}`}
                    >
                        <div className="flex flex-col md:flex-row md:items-start gap-4">
                            <div className="flex-1 min-w-0">
                                <div className="flex items-center gap-2 mb-2 flex-wrap">
                                    {!a.read && (
                                        <span className="w-2 h-2 rounded-full bg-amber-500 animate-pulse" />
                                    )}
                                    <span className="text-[10px] font-bold uppercase tracking-wider px-2.5 py-1 rounded-full bg-amber-50 text-amber-700 border border-amber-200">
                                        <Trophy size={10} className="inline mr-1 -mt-0.5" />
                                        Adjudicación nueva
                                    </span>
                                    <span className="text-[11px] font-bold text-ink-700">
                                        {a.competitor_name || a.party_name}
                                    </span>
                                    {a.award_date && (
                                        <span className="text-[10px] font-mono font-bold text-ink-500 bg-ink-50 px-2 py-1 rounded-full">
                                            {a.award_date}
                                        </span>
                                    )}
                                </div>
                                <h3 className="font-display text-lg font-bold text-ink-950 leading-snug mb-2 line-clamp-2">
                                    {a.tender_title}
                                </h3>
                                <div className="flex items-start gap-1.5 text-sm text-ink-500 font-medium mb-2">
                                    <Building2 size={12} className="text-ink-400 mt-0.5 shrink-0" />
                                    <span className="line-clamp-1">
                                        {a.tender_organo || "—"}
                                    </span>
                                </div>
                                <div className="flex items-center gap-4 text-sm flex-wrap">
                                    <span className="font-mono font-bold text-emerald-700">
                                        Adjudicado {fmtEUR(a.awarded_amount)}
                                    </span>
                                    {typeof a.budget === "number" && (
                                        <span className="font-mono text-ink-500">
                                            sobre {fmtEUR(a.budget)}
                                        </span>
                                    )}
                                    {typeof a.discount_pct === "number" && (
                                        <span className="font-mono font-bold text-violet-700 bg-violet-50 px-2 py-0.5 rounded-full">
                                            Baja {a.discount_pct}%
                                        </span>
                                    )}
                                    {a.tender_link && (
                                        <a
                                            href={a.tender_link}
                                            target="_blank"
                                            rel="noopener noreferrer"
                                            onClick={(e) => e.stopPropagation()}
                                            className="inline-flex items-center gap-1 text-brand-600 font-bold hover:underline"
                                            data-testid={`competitor-alert-link-${a.id}`}
                                        >
                                            Ver en PLCSP <ExternalLink size={11} />
                                        </a>
                                    )}
                                </div>
                            </div>
                        </div>
                    </div>
                ))}
            </div>
        </>
    );
};

